import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import CompareTray from "./components/CompareTray";
import TutorialPill from "./components/TutorialPill";
import DomTranslator from "./i18n/dom/DomTranslator";
import BottomNavMobile from "./components/BottomNavMobile";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
// Lazy — pulls in UnifiedPaymentDialog (~15KB gz). Not on the LCP path,
// only opened when a user hits an AI credits quota → defer to a chunk.
const AiCreditsUpsellDialog = lazy(() => import("./components/AiCreditsUpsellDialog"));
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";
import SiteJsonLd from "./components/SiteJsonLd";
import CanonicalUrl from "./components/CanonicalUrl";
import HreflangTags from "./components/HreflangTags";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { ThemeProvider } from "./contexts/ThemeContext";
// Lazy — same reasoning as above, opened only from the pro dashboard flow.
const UpgradeToAnnualDialog = lazy(() => import("./components/UpgradeToAnnualDialog"));
import ErrorBoundary from "./components/ErrorBoundary";

// Eager: home is the LCP entrypoint. Everything else is code-split per route.
import Home from "./pages/Home";
import VerifyEmail from "./pages/VerifyEmail";

const Properties = lazy(() => import("./pages/Properties"));
const PropertyDetails = lazy(() => import("./pages/PropertyDetails"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Marketing = lazy(() => import("./pages/Marketing"));
const Partners = lazy(() => import("./pages/Partners"));
const Developments = lazy(() => import("./pages/Developments"));
const LoanCalculatorPage = lazy(() => import("./pages/LoanCalculatorPage"));
const Estimation = lazy(() => import("./pages/Estimation"));
const BusinessIntelligence = lazy(() => import("./pages/BusinessIntelligence"));
const AgentPro = lazy(() => import("./pages/AgentPro"));
const PremiumSale = lazy(() => import("./pages/PremiumSale"));
const MarketReports = lazy(() => import("./pages/MarketReports"));
const Agents = lazy(() => import("./pages/Agents"));
const AgentProfile = lazy(() => import("./pages/AgentProfile"));
const AgentCard = lazy(() => import("./pages/AgentCard"));
const LegalProfessionals = lazy(() => import("./pages/LegalProfessionals"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const LegalProProfile = lazy(() => import("./pages/LegalProProfile"));
const Services = lazy(() => import("./pages/Services"));
const ServiceProviderProfile = lazy(() => import("./pages/ServiceProviderProfile"));
const MissionTracking = lazy(() => import("./pages/MissionTracking"));
const RenewListing = lazy(() => import("./pages/RenewListing"));
const ProCard = lazy(() => import("./pages/ProCard"));
const ReviewThankYou = lazy(() => import("./pages/ReviewThankYou"));
const BecomePro = lazy(() => import("./pages/BecomePro"));
const ProDashboard = lazy(() => import("./pages/ProDashboard"));
const SponsorFinancialPartner = lazy(() => import("./pages/SponsorFinancialPartner"));
const Blog = lazy(() => import("./pages/Blog"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const PaymentDemo = lazy(() => import("./pages/PaymentDemo"));
const MySubscriptions = lazy(() => import("./pages/MySubscriptions"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const BankRepossessions = lazy(() => import("./pages/BankRepossessions"));
const PublishPlans = lazy(() => import("./pages/PublishPlans"));
const AdminInviteAccept = lazy(() => import("./pages/AdminInviteAccept"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const SelectRole = lazy(() => import("./pages/SelectRole"));
const AllPlans = lazy(() => import("./pages/AllPlans"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const DevelopersPage = lazy(() => import("./pages/DevelopersPage"));
const ApiTermsPage = lazy(() => import("./pages/ApiTermsPage"));

// Lightweight route fallback to avoid layout shift while the chunk loads.
// BUG-01 — Route-aware skeleton: /dashboard renders a proper skeleton shell
// (matches the layout) instead of the generic spinner, so slow-network users
// see meaningful placeholder content during the lazy-chunk download window.
const DashboardRouteFallback = () => (
  <div className="min-h-dvh bg-gray-50" data-testid="dashboard-route-fallback">
    <div className="max-w-7xl mx-auto px-4 py-8 pb-24 md:pb-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-1">Tableau de bord</h1>
      <div className="rounded-xl ring-1 ring-stone-200 bg-stone-50 p-5 mt-3 mb-8 h-[112px] animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[104px] rounded-xl bg-white border border-stone-200 animate-pulse" />
        ))}
      </div>
      <div className="h-4 w-64 bg-stone-100 rounded animate-pulse mb-6" />
      <div className="h-[72px] rounded-xl bg-white border border-stone-200 animate-pulse mb-6" />
      <div className="h-10 w-full rounded-md bg-stone-200 animate-pulse mb-4" />
      <div className="min-h-[600px] rounded-xl bg-white border border-stone-200 animate-pulse" />
    </div>
  </div>
);

const RouteFallback = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/dashboard') || path.startsWith('/admin')) {
    return <DashboardRouteFallback />;
  }
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
      <div className="flex items-center gap-2 text-stone-500">
        <span className="inline-block w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
        <span className="text-sm">Chargement…</span>
      </div>
    </div>
  );
};

// Layout shell — hides the global Navbar/Footer/CompareTray on standalone
// public pages (e.g., agent digital cards on `/a/:slug`) to keep the
// shareable experience clean.
const ChromeShell = ({ children }) => {
  const { pathname } = useLocation();
  const isStandalone = pathname.startsWith("/a/") || pathname.startsWith("/pro-card/") || pathname.startsWith("/reviews/");
  return (
    <>
      {/* BUG-010 fix + cascade — WCAG 2.4.1 (Bypass Blocks, level A).
          Group of skip links revealed one-at-a-time as the user Tabs.
          Pattern popularisé par gov.uk / GitHub. Each `sr-only` link
          becomes `focus:not-sr-only` (pill top-left) when focused, so
          Tab #1 = "contenu", Tab #2 = "filtres", Tab #3 = "annonces",
          Tab #4 = "footer". If a target ID doesn't exist on the
          current page (e.g. /auth has no filters), the anchor is a
          no-op — but keeping the same DOM structure keeps Tab-order
          predictable across the site. */}
      <nav aria-label="Liens d'évitement" data-testid="skip-links-nav">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-600 focus:text-white focus:font-semibold focus:rounded-lg focus:shadow-xl focus:outline-none focus:ring-2 focus:ring-brand-300"
          data-testid="skip-link"
        >
          Aller au contenu principal
        </a>
        <a
          href="#filters-section"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-600 focus:text-white focus:font-semibold focus:rounded-lg focus:shadow-xl focus:outline-none focus:ring-2 focus:ring-brand-300"
          data-testid="skip-link-filters"
        >
          Aller aux filtres
        </a>
        <a
          href="#listings-section"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-600 focus:text-white focus:font-semibold focus:rounded-lg focus:shadow-xl focus:outline-none focus:ring-2 focus:ring-brand-300"
          data-testid="skip-link-listings"
        >
          Aller aux annonces
        </a>
        <a
          href="#footer-section"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-600 focus:text-white focus:font-semibold focus:rounded-lg focus:shadow-xl focus:outline-none focus:ring-2 focus:ring-brand-300"
          data-testid="skip-link-footer"
        >
          Aller au pied de page
        </a>
      </nav>
      {!isStandalone && <Navbar />}
      <main id="main-content" tabIndex="-1" className="outline-none">
        <DomTranslator />
        {!isStandalone && <TutorialPill />}
        {children}
      </main>
      {!isStandalone && <Footer />}
      {!isStandalone && <CompareTray />}
      {!isStandalone && <BottomNavMobile />}
      {!isStandalone && <PwaInstallPrompt />}
    </>
  );
};

function App() {
  // Dismiss the pre-hydration splash screen (see public/index.html
  // #realty237-splash) as soon as React has mounted its first render.
  // A ~150ms delay lets Home's LCP settle so the transition feels
  // graceful instead of ripping the splash off mid-animation.
  useEffect(() => {
    const t = setTimeout(() => {
      document.body.classList.add('r237-app-ready');
    }, 150);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <CurrencyProvider>
          <ScrollToTop />
          <SiteJsonLd />
          <CanonicalUrl />
          <HreflangTags />
          <ChromeShell>
          <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/properties" element={<Properties />} />
              <Route path="/property/:id" element={<PropertyDetails />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/auth/select-role" element={<SelectRole />} />
              <Route path="/forfaits" element={<AllPlans />} />
              <Route path="/developers" element={<DevelopersPage />} />
              <Route path="/legal/api-terms" element={<ApiTermsPage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/marketing" element={<Marketing />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/developments" element={<Developments />} />
              <Route path="/loan-calculator" element={<LoanCalculatorPage />} />
              <Route path="/estimation" element={<Estimation />} />
              <Route path="/business-intelligence" element={<BusinessIntelligence />} />
              <Route path="/agent-pro" element={<AgentPro />} />
              <Route path="/sell-with-us" element={<PremiumSale />} />
              <Route path="/premium-sale" element={<PremiumSale />} />
              <Route path="/market-reports" element={<MarketReports />} />
              <Route path="/agents" element={<Agents />} />
              <Route path="/agents/:id" element={<AgentProfile />} />
              <Route path="/a/:slug" element={<AgentCard />} />
              <Route path="/legal-pros" element={<LegalProfessionals />} />
              <Route path="/tutoriels" element={<Tutorials />} />
              <Route path="/devenir-partenaire" element={<BecomePartner />} />
              <Route path="/legal-pros/:id" element={<LegalProProfile />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:id" element={<ServiceProviderProfile />} />
              <Route path="/mission" element={<MissionTracking />} />
              <Route path="/mission/:reference" element={<MissionTracking />} />
              <Route path="/renew-listing" element={<RenewListing />} />
              <Route path="/pro-card/:id" element={<ProCard />} />
              <Route path="/reviews/thank-you" element={<ReviewThankYou />} />
              <Route path="/become-pro" element={<BecomePro />} />
              <Route path="/pro-dashboard" element={<ProDashboard />} />
              <Route path="/partenaires-financiers/sponsoriser" element={<SponsorFinancialPartner />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<ArticleDetail />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/payments/demo" element={<PaymentDemo />} />
              <Route path="/dashboard/subscriptions" element={<MySubscriptions />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/reprises-bancaires" element={<BankRepossessions />} />
              <Route path="/publier" element={<PublishPlans />} />
              <Route path="/admin-invite/:token" element={<AdminInviteAccept />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              {/* BUG-001 — Catch-all 404 (MUST stay last). Anything not
                  matched above renders NotFoundPage so the visitor sees
                  a clear "Page introuvable" with rescue links instead of
                  a blank screen between header and newsletter. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </ChromeShell>
        <Toaster />
        <SonnerToaster position="bottom-center" />
        {/* Payment-related dialogs — lazy so their code (UnifiedPaymentDialog,
            merchant code UI, Stripe wrapper) stays out of the LCP bundle.
            Suspense fallback is null since they are portals that only
            render when triggered by a global event bus. */}
        <Suspense fallback={null}>
          <AiCreditsUpsellDialog />
          <UpgradeToAnnualDialog />
        </Suspense>
        <CookieConsent />
        </CurrencyProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
